/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import type React from 'react';
import { useEffect, useState } from 'react';
import { authorizeApp, buildAuthorizeUrl, login, resend2FACode, submitConsent } from '../api/auth';
import EEImage from '../assets/EE_Image.png';
import {
  CLIENT_ID,
  GOOGLE_CLIENT_ID,
  IFTTT_SCOPE,
  PIPEDRIVE_CLIENT_ID,
  PIPEDRIVE_SCOPE,
  REDIRECT_URI,
  SCOPES,
  SUPABASE_SCOPE,
} from '../config';
import LoginForm from './LoginForm';

const APP_TYPE_DEFAULTS: Record<string, { clientId: string; scope: string }> = {
  WIX: { clientId: '', scope: '' },
  SHOPIFY: { clientId: '', scope: '' },
  PIPEDRIVE: { clientId: PIPEDRIVE_CLIENT_ID, scope: PIPEDRIVE_SCOPE },
  BigCommerce: { clientId: '', scope: '' },
  JOTFORM: { clientId: '', scope: '' },
  ifttt: { clientId: CLIENT_ID, scope: IFTTT_SCOPE },
  supabase: { clientId: CLIENT_ID, scope: SUPABASE_SCOPE },
};

const APP_TYPES_NEEDING_CODE_IN_JS = ['PIPEDRIVE', 'BigCommerce', 'JOTFORM', 'ifttt', 'supabase', 'WIX'];

const OAuthFlow: React.FC = () => {
  const [alert, setAlert] = useState<{
    tone: 'info' | 'positive' | 'warn' | 'critical';
    message: string;
  } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [_loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [redirect, setRedirect] = useState(false);
  const [by2FA, setby2FA] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userGoogleCode, setuserGoogleCode] = useState('');
  const [globalAppType, setGlobalAppType] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [twoFAError, setTwoFAError] = useState<string | null>(null);
  const [EELogo, setEELogo] = useState(true);
  const [showResendBanner, setShowResendBanner] = useState(false);
  const [showErrorResendBanner, setErrorShowResendBanner] = useState(false);

  const handleLogin = async ({ email, password }: { email: string; password: string }) => {
    setIsLoggingIn(true);
    setLoginError(null);
    setUserName(email);
    setUserPassword(password);
    try {
      const result = await login({ username: email, password });
      if (result.status === 'TwoFactorCodeRequired' || result.status === 'TwoFactorCodeExpired' || result.status === 'TwoFactorCodeIncorrect') {
        setby2FA(true);
        return;
      }

      if (result.bearerToken) {
        localStorage.setItem('bearerToken', result.bearerToken);
        window.parent.postMessage(
          {
            type: 'CANVA_REQUEST_AUTH',
            payload: {
              provider: 'ElasticEmail',
              scopes: SCOPES,
            },
          },
          '*'
        );
        setIsAuthenticated(true);
        setRedirect(true);
        setAlert({ tone: 'positive', message: 'Logged in successfully!' });
      } else {
        throw new Error('Login failed - no bearer token received');
      }
    } catch (error: any) {
      let errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
      if (errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('incorrect')) {
        errorMessage = 'Incorrect email or password.';
      }
      setLoginError(errorMessage);
      setAlert({
        tone: 'critical',
        message: errorMessage,
      });
      setIsLoggingIn(false);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse: any) => {
    setIsLoggingIn(true);
    try {
      const googleIdToken = credentialResponse.credential;
      if (!googleIdToken) throw new Error('No Google ID token received');
      setuserGoogleCode(googleIdToken);
      const result = await login({ googleIdToken });
      if (result.status === 'TwoFactorCodeRequired' || result.status === 'TwoFactorCodeExpired' || result.status === 'TwoFactorCodeIncorrect') {
        setby2FA(true);
        return;
      }
      if (result.bearerToken) {
        localStorage.setItem('bearerToken', result.bearerToken);
        setIsAuthenticated(true);
        setRedirect(true);
        // setAlert({ tone: 'positive', message: 'Google login successful!' });
      } else {
        throw new Error('Login failed - no bearer token received');
      }
    } catch (error: any) {
      setAlert({
        tone: 'critical',
        message: error instanceof Error ? error.message : 'Login failed. Please try again.',
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleTwoFALogin = async (twoFaCode: any) => {
    try {
      setIsLoggingIn(true);
      const twoFaToken = twoFaCode;
      if (!twoFaToken) throw new Error('No 2FA code received');
      const result = await login({
        twoFaToken,
        googleIdToken: userGoogleCode,
        username: userName,
        password: userPassword,
      });
      if (result.bearerToken) {
        localStorage.setItem('bearerToken', result.bearerToken);
        setIsAuthenticated(true);
        setRedirect(true);
      } else {
        throw new Error('Login failed - no bearer token received');
      }
      setIsLoggingIn(false);
    } catch (error: any) {
      setTwoFAError(error.message);
      setAlert({
        tone: 'critical',
        message: error instanceof Error ? error.message : 'Login failed. Please try again.',
      });
      setIsLoggingIn(false);
    }
  };

  const handleResendTwoFA = async () => {
    try {
      await resend2FACode({
        googleIdToken: userGoogleCode,
        userName: userName,
        password: userPassword,
      });

      setShowResendBanner(true);

      setTimeout(() => {
        setShowResendBanner(false);
      }, 2000);
    } catch (error: any) {
      setErrorShowResendBanner(true);
      setTimeout(() => {
        setErrorShowResendBanner(false);
      }, 2000);
      setTwoFAError(error?.message);
    }
  };

  const handleRedirectLogin = async () => {
    setIsLoggingIn(false);
    setby2FA(false);
  };

  const handleRememberMe = async (_rememberLogin: boolean) => {};

  const getAuthParams = (urlParams: URLSearchParams, appType: string) => {
    const redirectUri = urlParams.get('redirect_uri') || REDIRECT_URI;
    const defaults = APP_TYPE_DEFAULTS[appType] || { clientId: CLIENT_ID, scope: SCOPES.join(' ') };
    const clientId = urlParams.get('client_id') || defaults.clientId;
    const scope = urlParams.get('scope') || defaults.scope;
    const randomState = Math.random().toString(36).substring(2, 15);
    let state: string;
    if (appType === 'WIX') {
      state = randomState;
    } else if (appType === 'ifttt') {
      state = urlParams.get('state') || '';
    } else {
      state = urlParams.get('state') || randomState;
    }

    const responseType = urlParams.get('response_type') || 'code';
    const codeChallenge = urlParams.get('code_challenge') || undefined;
    const codeChallengeMethod = urlParams.get('code_challenge_method') || undefined;
    const resource = urlParams.get('resource') || undefined;
    const prompt = urlParams.get('prompt') || undefined;

    return { redirectUri, clientId, scope, state, responseType, codeChallenge, codeChallengeMethod, resource, prompt };
  };

  const runOAuthFlow = async () => {
    setLoading(true);
    setLoginError(null);
    setEELogo(false);

    try {
      setIsLoggingIn(true);
      const bearerToken = localStorage.getItem('bearerToken');

      if (!bearerToken) {
        setAlert({
          tone: 'critical',
          message: 'No authentication token found. Please login first.',
        });
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const { redirectUri, clientId, scope, state, responseType, codeChallenge, codeChallengeMethod, resource, prompt } =
        getAuthParams(urlParams, globalAppType);
      const authParams = { clientId, redirectUri, scopes: scope, state, responseType, codeChallenge, codeChallengeMethod, resource, prompt };

      if (!APP_TYPES_NEEDING_CODE_IN_JS.includes(globalAppType)) {
        window.location.href = buildAuthorizeUrl(authParams);
        return;
      }

      const authResponse = await authorizeApp({ bearerToken, ...authParams });

      let authCode: string;
      let appAccess: string[];

      if (authResponse.ConsentRequired) {
        const { SessionKey } = authResponse;
        if (!SessionKey) {
          throw new Error('No session key in response');
        }
        const consentResponse = await submitConsent({
          bearerToken,
          sessionKey: SessionKey,
          consentGiven: true,
        });
        appAccess = consentResponse?.granted_scopes || [];
        if (!consentResponse.consent_given) {
          throw new Error('User denied consent');
        }
        authCode = consentResponse.code || '';
        if (!authCode) {
          throw new Error('No authorization code received after consent');
        }
      } else {
        authCode = authResponse.Code || '';
        appAccess = authResponse.grantedScopes || [];

        if (!authCode) {
          throw new Error('No authorization code received');
        }
      }

      if (globalAppType === 'PIPEDRIVE' || globalAppType === 'BigCommerce') {
        window.opener.postMessage({ type: 'code', code: authCode, access: appAccess }, '*');
        window.close();
      } else if (globalAppType === 'ifttt') {
        window.location.href = `${redirectUri}?state=${encodeURIComponent(state)}&code=${encodeURIComponent(authCode)}`;
      } else if (globalAppType === 'supabase') {
        window.location.href = `${redirectUri}/supabase?code=${encodeURIComponent(authCode)}&state=${encodeURIComponent(state)}`;
      } else {
        window.location.href = `${redirectUri}?code=${encodeURIComponent(authCode)}&state=${encodeURIComponent(state)}`;
      }
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Authorization failed. Please try again.';
      setLoginError(errorMessage);
      setAlert({ tone: 'critical', message: errorMessage });
      setEELogo(false);
      setIsLoggingIn(false);
      setRedirect(false);
    } finally {
      localStorage.removeItem('bearerToken');
      setLoading(false);
      setIsLoggingIn(false);
    }
  };

  useEffect(() => {
    if (alert) {
      const timeout = setTimeout(() => setAlert(null), 3000);
      return () => clearTimeout(timeout);
    }
  }, [alert]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('client_id');
    let appType: string;
    if (clientId === CLIENT_ID) {
      appType = 'Canva';
    } else {
      appType = urlParams.get('app_type') || '';
    }
    setGlobalAppType(appType);
    const bearerToken = localStorage.getItem('bearerToken');
    if (bearerToken) {
      setIsAuthenticated(true);
    }
    const oauthAccessToken = localStorage.getItem('oauthAccessToken');
    if (oauthAccessToken) {
      setIsAuthorized(true);
      setAccessToken(oauthAccessToken);
    }
  }, []);

  return (
    <>
      {showResendBanner && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            padding: '8px 12px',
            fontSize: '14px',
            display: 'inline-block',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          Resend code has been sent successfully
        </div>
      )}

      {showErrorResendBanner && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#d4edda',
            color: '#fd2c2cff',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            padding: '8px 12px',
            fontSize: '14px',
            display: 'inline-block',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          Code resend attempts limit reach.
        </div>
      )}
      <div style={{ maxWidth: 400, margin: '40px auto', padding: 24 }}>
        {EELogo && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              height: '100px',
            }}
          >
            <img
              src={EEImage}
              alt="Elastic Email logo"
            />
          </div>
        )}
        {!isAuthorized && (
          <>
            {!isAuthenticated && !by2FA && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '-50px',
                  marginTop: '30px',
                }}
              >
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={() =>
                      setAlert({
                        tone: 'critical',
                        message: 'Login failed. Please try again.',
                      })
                    }
                    width="100%"
                  />
                </GoogleOAuthProvider>
              </div>
            )}

            <LoginForm
              onLogin={handleLogin}
              onGoogleLogin={handleGoogleLogin}
              LoggedIn={redirect}
              visibility2FA={by2FA}
              onVerify={(code) => handleTwoFALogin(code)}
              onResend={() => handleResendTwoFA()}
              redirectLogin={() => handleRedirectLogin()}
              rememberLogin={handleRememberMe}
              onAuthorizeApp={() => runOAuthFlow()}
              isLoading={isLoggingIn}
              googleClientId={GOOGLE_CLIENT_ID}
              enableGoogleLogin={false}
              appName={globalAppType}
              loginError={loginError}
              twoFAError={twoFAError}
            />
          </>
        )}

        {isAuthorized && accessToken && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#f0fff0',
                borderRadius: '4px',
              }}
            >
              <small>✅ User is authorized! You can now use your access token.</small>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OAuthFlow;
